import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileText, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import {
  LMS_SUBMISSION_TYPES,
  LmsAssignment,
  LmsAssignmentStatus,
  LmsAssignmentsApi,
  LmsSubmissionType,
} from '@/services/lms.api';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea, Checkbox } from '@/components/ui/Input';
import { formatDateTime, formatRelative } from '@/lib/format';
import { toast } from '@/stores/toast.store';
import { usePermissions } from '@/hooks/usePermissions';
import { LmsSubmissionsModal } from './LmsSubmissionsModal';

const statusTone: Record<LmsAssignmentStatus, 'slate' | 'green'> = { draft: 'slate', published: 'green' };

const SUBMISSION_TYPE_LABEL: Record<LmsSubmissionType, string> = {
  text: 'Text',
  file: 'File',
  both: 'Text + File',
};

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

function toIso(datetimeLocalValue: string): string {
  return new Date(datetimeLocalValue).toISOString();
}

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const schema = z
  .object({
    title: z.string().min(1, 'Required').max(200),
    instructions: z.string().optional().or(z.literal('')),
    maxMarks: z.string().optional().or(z.literal('')),
    dueAt: z.string().optional().or(z.literal('')),
    allowLate: z.boolean(),
    submissionType: z.enum(LMS_SUBMISSION_TYPES),
  })
  .superRefine((v, ctx) => {
    if (v.maxMarks?.trim()) {
      const n = Number(v.maxMarks);
      if (!Number.isInteger(n) || n < 1) {
        ctx.addIssue({ path: ['maxMarks'], code: z.ZodIssueCode.custom, message: 'Must be a positive whole number' });
      }
    }
  });
type FormValues = z.infer<typeof schema>;

export function LmsCourseAssignmentsTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [modal, setModal] = useState<{ open: boolean; assignment?: LmsAssignment }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<LmsAssignment | null>(null);
  const [submissionsFor, setSubmissionsFor] = useState<LmsAssignment | null>(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-assignments', courseId, page, limit],
    queryFn: () => LmsAssignmentsApi.list(courseId, { page, limit }),
    placeholderData: keepPreviousData,
  });

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: ReturnType<typeof buildPayload> }) =>
      v.id ? LmsAssignmentsApi.update(v.id, v.payload) : LmsAssignmentsApi.create(courseId, v.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-assignments', courseId] });
      setModal({ open: false });
      toast.success('Assignment saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => LmsAssignmentsApi.publish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-assignments', courseId] });
      toast.success('Assignment published');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Publish failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => LmsAssignmentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-assignments', courseId] });
      setConfirmDelete(null);
      toast.success('Assignment deleted');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const rows = pageData?.items ?? [];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">Assignments for this course.</p>
        <button className="btn-primary" onClick={() => setModal({ open: true })}>
          <Plus className="mr-1.5 h-4 w-4" /> New assignment
        </button>
      </div>

      <DataTable<LmsAssignment>
        rows={rows}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No assignments yet."
        columns={[
          {
            key: 'title',
            header: 'Title',
            render: (a) => (
              <button
                className="font-medium text-slate-900 hover:text-brand-700 hover:underline"
                onClick={() => setSubmissionsFor(a)}
              >
                {a.title}
              </button>
            ),
          },
          { key: 'status', header: 'Status', render: (a) => <Badge tone={statusTone[a.status]}>{a.status}</Badge> },
          { key: 'submissionType', header: 'Type', render: (a) => SUBMISSION_TYPE_LABEL[a.submissionType] },
          { key: 'maxMarks', header: 'Marks', render: (a) => (a.maxMarks == null ? 'Ungraded' : a.maxMarks) },
          {
            key: 'dueAt',
            header: 'Due',
            render: (a) =>
              a.dueAt ? (
                <span title={formatDateTime(a.dueAt)}>{formatRelative(a.dueAt)}</span>
              ) : (
                '—'
              ),
          },
          {
            key: 'allowLate',
            header: 'Late OK',
            render: (a) => (a.dueAt ? (a.allowLate ? <Badge tone="blue">Yes</Badge> : <Badge tone="slate">No</Badge>) : '—'),
          },
        ]}
        actions={(a) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setSubmissionsFor(a)}
              title="View submissions"
            >
              <FileText className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, assignment: a })}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {a.status === 'draft' && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-green-600 disabled:opacity-40"
                onClick={() => publish.mutate(a.id)}
                disabled={publish.isPending}
                title="Publish"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
            {isAdmin && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirmDelete(a)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setPage(1);
            setLimit(l);
          }}
        />
      )}

      {modal.open && (
        <AssignmentFormModal
          open={modal.open}
          assignment={modal.assignment}
          onClose={() => setModal({ open: false })}
          saving={upsert.isPending}
          onSubmit={(v) => upsert.mutate({ id: modal.assignment?.id, payload: buildPayload(v) })}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        loading={remove.isPending}
        title="Delete assignment?"
        message={`Soft-delete "${confirmDelete?.title}". Existing submissions remain in the database.`}
        confirmText="Delete assignment"
      />

      {submissionsFor && (
        <LmsSubmissionsModal assignment={submissionsFor} onClose={() => setSubmissionsFor(null)} />
      )}
    </>
  );
}

function buildPayload(v: FormValues) {
  return {
    title: v.title,
    instructions: v.instructions?.trim() ? v.instructions : undefined,
    maxMarks: v.maxMarks?.trim() ? Number(v.maxMarks) : undefined,
    dueAt: v.dueAt?.trim() ? toIso(v.dueAt) : undefined,
    allowLate: v.dueAt?.trim() ? v.allowLate : false,
    submissionType: v.submissionType,
  };
}

function AssignmentFormModal({
  open,
  assignment,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  assignment?: LmsAssignment;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
}) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      title: assignment?.title ?? '',
      instructions: assignment?.instructions ?? '',
      maxMarks: assignment?.maxMarks != null ? String(assignment.maxMarks) : '',
      dueAt: toDatetimeLocal(assignment?.dueAt),
      allowLate: assignment?.allowLate ?? false,
      submissionType: assignment?.submissionType ?? 'both',
    },
  });
  const dueAt = watch('dueAt');

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={assignment ? `Edit ${assignment.title}` : 'New assignment'}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : assignment ? 'Save changes' : 'Create assignment'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        <Field label="Title" required error={errors.title?.message}>
          <Input {...register('title')} placeholder="Essay 1" />
        </Field>
        <Field label="Instructions" hint="Optional">
          <Textarea rows={3} {...register('instructions')} placeholder="What students should submit…" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Max marks" error={errors.maxMarks?.message} hint="Optional — leave blank for ungraded">
            <Input type="number" min={1} {...register('maxMarks')} placeholder="e.g. 20" />
          </Field>
          <Field label="Submission type" required>
            <Select {...register('submissionType')}>
              {LMS_SUBMISSION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SUBMISSION_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Due at" hint="Optional">
          <Input type="datetime-local" {...register('dueAt')} />
        </Field>
        <Checkbox
          label="Allow late submissions"
          disabled={!dueAt}
          title={!dueAt ? 'Only meaningful once a due date is set' : undefined}
          {...register('allowLate')}
        />
      </form>
    </Modal>
  );
}
